import { multilingualFieldSchema } from "@/i18n";
import api from "@/lib/axiosConfig.ts";
import { defaultPositionValues } from "@/lib/image";
import { PublicationStatus } from "@/lib/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

export interface AttachmentDTO {
  where: any;
  order: any;
  //take: number;
  //skip: number;
}

export const attachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  position: z.string(),
  caption: multilingualFieldSchema(z.string()).optional(),
  description: multilingualFieldSchema(z.string()).optional(),
  textAlt: multilingualFieldSchema(z.string()).optional(),
  details: z.string().optional().nullable(),
  mimeType: z.string().optional(),
  docId: z.string(),
  hdDocId: z.string().optional(),
  order: z.number().optional(),
  mediumPublicUrl: z.string().optional(),
  largePublicUrl: z.string().optional(),
  publicUrl: z.string().optional(),

  isPublic: z.boolean().optional(),
  published: z.boolean().optional().default(false),
  isHd: z.boolean().optional(),
  mimetype: z.string().optional(),
});

export type Attachment = z.infer<typeof attachmentSchema>;

export const formImageInputSchema = z.object({
  base64Value: z.string().optional(),
  attachmentId: z.string().optional(),
});

export const ALL_ATTACHMENT_FORMATS = ["small", "medium", "large"] as const;

export type AttachmentFormat = (typeof ALL_ATTACHMENT_FORMATS)[number];

export const ATTACHMENT_FORMAT_CONVERSION = Object.freeze<
  Record<AttachmentFormat, string>
>({
  small: "petit",
  medium: "moyen",
  large: "large",
});

export const PUBLIC_ATTACHMENT_FORMATS = [
  "small",
  "medium",
] as const satisfies AttachmentFormat[];

export type PublicAttachmentFormat = Extract<
  AttachmentFormat,
  (typeof PUBLIC_ATTACHMENT_FORMATS)[number]
>;

export type AttachmentPublicUrls = Record<
  PublicAttachmentFormat,
  string | null
>;

async function getAttachments({ where, order }: AttachmentDTO) {
  const { data } = await api.post<Attachment[]>(
    `back/api/attachment`,
    {
      where,
      orderBy: order,
    },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useGetAllAttachments = ({ where, order }: AttachmentDTO) => {
  return useQuery({
    queryKey: ["artworks", "attachments", where, order],
    queryFn: () => getAttachments({ where, order }),
  });
};

export async function getAttachmentById(id: string) {
  const { data } = await api.get(`back/api/attachment/id/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export const useGetAttachmentById = (id: string) => {
  return useQuery({
    queryKey: ["attachment", id],
    queryFn: () => getAttachmentById(id),
    enabled: !!id,
  });
};

export async function getAttachmentsByDocId(id: string | undefined) {
  if (!id) {
    return [];
  }
  const { data } = await api.get<Attachment[]>(`back/api/attachment/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export const useGetAttachmentsByDocId = (id: string | undefined) => {
  return useQuery({
    queryKey: ["attachment", { docId: id }],
    queryFn: () => getAttachmentsByDocId(id),
    enabled: !!id,
  });
};

export interface UpdateAttachmentDTO {
  id: string;
  attachment: Attachment;
}

export async function updateAttachment({
  id,
  attachment,
}: UpdateAttachmentDTO) {
  const { data } = await api.put(
    `back/api/attachment/${id}`,
    {
      attachment: {
        ...attachment,
        id: id ?? "",
      },
    },
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
  return data;
}

export const useUpdateAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

async function deleteAttachment(id: string) {
  const { data } = await api.delete(`back/api/attachment/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export const useDeleteAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

async function deleteHdAttachment(id: string) {
  const { data } = await api.delete(`back/api/attachment/hd/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
  return data;
}

export const useDeleteHdAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHdAttachment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
    },
  });
};

async function getPublicUrl(id: string | undefined) {
  if (!id) {
    return null;
  }
  return await api.get(`back/api/attachment/share/${id}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
}

export const useGetPublicUrl = (id: string | undefined) => {
  return useQuery({
    queryKey: ["attachment", id],
    queryFn: () => getPublicUrl(id),
    enabled: !!id,
  });
};

export async function getAttachmentBiggestWeight(attachmentId: string) {
  if (!attachmentId) {
    return null;
  }
  return await api.get(`back/api/attachment/weight/${attachmentId}`, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
}


export function shouldIncludeAttachment(
  attachment: Attachment,
  publicationStatus: PublicationStatus,
) {
  return (
    attachment.published === true ||
    (publicationStatus === "published" &&
      (defaultPositionValues as string[]).includes(attachment.position))
  );
}
